/**
 * EPIC-050 STORY-2211 — Privacy Policy content per locale.
 *
 * Jurisdiction-scoping rule (CLAUDE.md, locked 2026-05-03): each
 * region's regulatory framework is referenced ONLY to users in that
 * jurisdiction in their locale. Cross-contamination must be avoided.
 *
 * Phase 1 (this PR): en + pt-BR scaffolds. Other locales fall back
 * to en until respective markets activate. The bodies below are
 * SCAFFOLD CONTENT marked DRAFT — all surfaces noting jurisdiction-
 * specific framework names ("LGPD" in pt-BR; generic "applicable
 * data-protection law" in en) need final lawyer review before Gate B
 * real-money launch:
 *   - Brazilian fintech lawyer for pt-BR (LGPD compliance)
 *   - Respective regional counsel when es-MX, en-CA, fr-CA, en-IN
 *     activate (LFPDPPP, PIPEDA, DPDPA)
 *
 * Pattern: each entry exposes `{ effectiveDate, summary, bodyMd }`.
 * The route renders bodyMd via the same lightweight markdown
 * renderer the /legal/tos page uses.
 */

export interface PrivacyPolicyVariant {
  /** ISO date string surfaced in the page header. */
  effectiveDate: string;
  /** One-line summary rendered as a callout above the body. */
  summary: string;
  /** Full policy body, markdown. Headings via `#` / `##`,
   *  paragraphs by blank line, lists by `-`. */
  bodyMd: string;
}

const EN_POLICY: PrivacyPolicyVariant = {
  effectiveDate: '2026-05-03',
  summary:
    'How Granbay collects, uses, and shares your information. Closed-beta version — DRAFT pending legal review before real-money launch.',
  bodyMd: `# Privacy Policy

_DRAFT — pending legal review before Gate B real-money launch._

## 1. Introduction

This Privacy Policy describes how Granbay ("we", "us") collects, uses, and shares information about you when you use our services. We comply with applicable data-protection laws in the jurisdictions where we operate.

## 2. Information We Collect

We collect the following categories of information:

- **Account information** — email address, username, display name, avatar.
- **Identity verification** — date of birth and country of residence (Tier 1 KYC). Government-issued ID and selfie when you initiate fiat withdrawal (Tier 2 KYC).
- **Trading activity** — every order, fill, position, and resolution outcome.
- **Device and usage data** — IP address, browser type, operating system, pages visited, referral URL.
- **Optional content** — profile bio, posts, comments, tips, copy-trader subscriptions.

## 3. How We Use Information

We use your information to:

- Operate the prediction-market platform (route orders, settle trades, fan out resolution events to your positions).
- Verify your identity to the extent required by applicable financial-services regulation in your jurisdiction.
- Detect and prevent fraud, abuse, and market manipulation.
- Improve the product, debug issues, and tune performance.
- Send transactional emails (email verification, password reset, withdrawal confirmation, market resolution).

## 4. Information Sharing

We do not sell your personal information. We share information only in these cases:

- **Service providers** — payment processors, identity-verification vendors, cloud-infrastructure providers, transactional-email providers. Each is bound by a data-processing agreement.
- **Legal compliance** — when required by valid legal process in a jurisdiction where we operate.
- **Safety** — to prevent imminent harm to users or to the platform itself.

## 5. Your Rights

Depending on your jurisdiction, you may have the right to:

- Access the personal information we hold about you.
- Request correction of inaccurate information.
- Request deletion of your account and associated personal information (subject to financial-records retention laws — see Section 7).
- Withdraw consent for optional processing.

The /settings/account page exposes self-service data export and account-deletion flows. For other rights requests, contact privacy@granbay.com.

## 6. Data Retention

- **Active account data** — retained for the life of the account.
- **Trading records and audit events** — retained for the period required by applicable financial-services regulation (typically 5–7 years post-account closure).
- **Marketing communications opt-outs** — retained indefinitely so we honour your preference.

## 7. Account Deletion

You may request account deletion at any time via /settings/account. Deletion executes after a 30-day grace period during which you may cancel. After execution, identifying fields are anonymized; trading records are retained per Section 6 for accounting and regulatory compliance.

## 8. International Transfers

Granbay's infrastructure runs on cloud providers with regional presence. Your data may be processed in jurisdictions outside your country of residence. Where required, we rely on standard contractual clauses or equivalent mechanisms.

## 9. Children

The service is not directed at users under 18. We do not knowingly collect information from minors.

## 10. Changes to This Policy

We post material changes to this page and notify active users via email. Continued use after the effective date of a change constitutes acceptance.

## 11. Contact

privacy@granbay.com`,
};

const PT_POLICY: PrivacyPolicyVariant = {
  effectiveDate: '2026-05-03',
  summary:
    'Como a Granbay coleta, usa e compartilha suas informações. Versão de beta fechado — RASCUNHO em conformidade com a LGPD, sujeito a revisão jurídica antes do lançamento real-money.',
  bodyMd: `# Política de Privacidade

_RASCUNHO — sujeito a revisão jurídica antes do lançamento real-money (Gate B)._

## 1. Introdução

Esta Política de Privacidade descreve como a Granbay ("nós") coleta, usa e compartilha informações sobre você ao utilizar nossos serviços. Cumprimos a Lei Geral de Proteção de Dados Pessoais brasileira (Lei nº 13.709/2018, "LGPD"), aplicável a usuários residentes no Brasil.

## 2. Informações Coletadas

Coletamos as seguintes categorias de informação:

- **Dados de conta** — endereço de e-mail, nome de usuário, nome de exibição, avatar.
- **Verificação de identidade** — data de nascimento e país de residência (KYC Tier 1). Documento de identidade emitido pelo governo e selfie quando você iniciar saque em fiat (KYC Tier 2).
- **Atividade de trading** — cada ordem, execução, posição e resultado de resolução.
- **Dados de dispositivo e uso** — endereço IP, tipo de navegador, sistema operacional, páginas visitadas, URL de origem.
- **Conteúdo opcional** — biografia de perfil, posts, comentários, gorjetas, assinaturas de copy-trader.

## 3. Bases Legais (LGPD Art. 7º)

Processamos seus dados pessoais com base em:

- **Execução de contrato** — para operar a plataforma e os serviços que você contrata.
- **Cumprimento de obrigação legal** — KYC, registros financeiros, regulação aplicável.
- **Legítimo interesse** — prevenção a fraude e manipulação de mercado.
- **Consentimento** — para comunicações de marketing opcional, claramente identificadas.

## 4. Uso das Informações

Usamos suas informações para:

- Operar a plataforma de prediction markets (rotear ordens, liquidar trades, distribuir eventos de resolução).
- Verificar sua identidade conforme exigido pela regulação financeira aplicável.
- Detectar e prevenir fraude, abuso e manipulação de mercado.
- Melhorar o produto, depurar problemas e ajustar performance.
- Enviar e-mails transacionais (verificação de e-mail, redefinição de senha, confirmação de saque, resolução de mercado).

## 5. Compartilhamento de Informações

Não vendemos seus dados pessoais. Compartilhamos informações apenas nos seguintes casos:

- **Operadores de tratamento** — processadores de pagamento, fornecedores de verificação de identidade, provedores de infraestrutura em nuvem, provedores de e-mail transacional. Cada um está vinculado a contrato de tratamento de dados.
- **Cumprimento legal** — quando exigido por processo legal válido em jurisdição onde operamos.
- **Segurança** — para prevenir dano iminente a usuários ou à plataforma.

## 6. Seus Direitos (LGPD Art. 18)

Você tem direito a:

- Confirmação da existência de tratamento.
- Acesso aos dados pessoais.
- Correção de dados incompletos, inexatos ou desatualizados.
- Anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade.
- Portabilidade dos dados a outro fornecedor de serviço.
- Eliminação dos dados pessoais tratados com seu consentimento.
- Informação sobre as entidades públicas e privadas com as quais compartilhamos seus dados.
- Revogação do consentimento.

A página /settings/account oferece autoatendimento para exportação de dados e exclusão de conta. Para outros pedidos de direitos, contate privacidade@granbay.com.

## 7. Retenção de Dados

- **Dados de conta ativa** — retidos pela vida da conta.
- **Registros de trading e eventos de auditoria** — retidos pelo período exigido pela regulação financeira aplicável (tipicamente 5–7 anos após o encerramento da conta).
- **Opt-outs de comunicações de marketing** — retidos indefinidamente para honrar sua preferência.

## 8. Exclusão de Conta

Você pode solicitar exclusão de conta a qualquer momento via /settings/account. A exclusão é executada após um período de 30 dias de carência durante o qual você pode cancelar. Após a execução, os campos identificadores são anonimizados; os registros de trading são retidos conforme a Seção 7 para conformidade contábil e regulatória.

## 9. Transferências Internacionais

A infraestrutura da Granbay roda em provedores de nuvem com presença regional. Seus dados podem ser processados em jurisdições fora do Brasil. Quando necessário, utilizamos cláusulas contratuais padrão ou mecanismos equivalentes nos termos da LGPD.

## 10. Crianças e Adolescentes

O serviço não é destinado a usuários menores de 18 anos. Não coletamos intencionalmente informações de menores.

## 11. Encarregado pelo Tratamento de Dados (DPO)

Conforme exigido pela LGPD Art. 41, nosso Encarregado pode ser contatado em: dpo@granbay.com.

## 12. Alterações nesta Política

Publicamos alterações materiais nesta página e notificamos usuários ativos por e-mail. O uso continuado após a data de vigência de uma alteração constitui aceitação.

## 13. Contato

privacidade@granbay.com`,
};

/**
 * Pick the variant for the user's locale. pt → LGPD-aware Portuguese.
 * Everything else → generic English (the `en` default uses generic
 * industry-standard phrasing per the locked jurisdiction-scoping rule;
 * it does NOT mention LGPD or any other framework name).
 *
 * When a future locale activates with its own jurisdiction (es-MX,
 * en-CA, fr-CA, en-IN), add a variant module here and a case below.
 */
export function privacyPolicyForLocale(locale: string): PrivacyPolicyVariant {
  if (locale === 'pt' || locale === 'pt-BR') return PT_POLICY;
  return EN_POLICY;
}
